import * as React from "react";

import { cn } from "../lib/cn";

export type StorizoneInteractiveLogoVariant = "lockup" | "mark";
export type StorizoneInteractiveLogoTheme = "light" | "dark";
export type StorizoneInteractiveLogoState =
  | "idle"
  | "loading"
  | "success"
  | "error";
export type StorizoneInteractiveLogoVisualState =
  | StorizoneInteractiveLogoState
  | "intro"
  | "hover"
  | "pressed"
  | "tap";

export interface StorizoneInteractiveLogoColors {
  brand?: string;
  accent?: string;
  ink?: string;
  success?: string;
  error?: string;
}

export interface StorizoneInteractiveLogoHandle {
  playIntro: () => void;
  playTap: () => void;
  element: HTMLSpanElement | null;
}

export interface StorizoneInteractiveLogoProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children" | "color"
> {
  /** Full horizontal lockup or compact symbol-only mark. */
  variant?: StorizoneInteractiveLogoVariant;
  /** Product-driven visual state. Success and error remain visible until this value changes. */
  state?: StorizoneInteractiveLogoState;
  /** Explicit logo palette, independent of the surrounding application theme. */
  theme?: StorizoneInteractiveLogoTheme;
  /** Enables hover, press, tap, and pointer-parallax behavior. */
  interactive?: boolean;
  /** Plays the modular assembly animation when the component mounts. */
  playIntro?: boolean;
  /** Change this value to replay the entrance without remounting the component. */
  introKey?: string | number;
  /** Forces the static reduced-motion treatment. The OS preference is always respected. */
  reducedMotion?: boolean;
  /** Width of the rendered lockup. Height follows the SVG aspect ratio. */
  width?: number | string;
  /** Accessible name used when aria-label is not supplied. */
  label?: string;
  /** Optional palette overrides. */
  colors?: StorizoneInteractiveLogoColors;
  /** Called when the visible interaction state changes. */
  onVisualStateChange?: (state: StorizoneInteractiveLogoVisualState) => void;
}

type LogoStyle = React.CSSProperties & {
  "--sz-logo-brand"?: string;
  "--sz-logo-accent"?: string;
  "--sz-logo-ink"?: string;
  "--sz-logo-success"?: string;
  "--sz-logo-error"?: string;
  "--sz-logo-pointer-x"?: number;
  "--sz-logo-pointer-y"?: number;
};

const LIGHT_COLORS = {
  brand: "#008ECC",
  accent: "#35C2FF",
  ink: "#08283D",
  success: "#10B981",
  error: "#EF5B5B",
} as const;

const DARK_COLORS = {
  brand: "#21B7F3",
  accent: "#7ADFFF",
  ink: "#F7FCFF",
  success: "#34D399",
  error: "#FF7A7A",
} as const;

const INTRO_DURATION = 1180;
const TAP_DURATION = 620;

const LOGO_STYLES = String.raw`
  .sz-interactive-logo {
    display: inline-block;
    max-width: 100%;
    color: var(--sz-logo-ink);
    line-height: 0;
    outline: none;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
  }

  .sz-interactive-logo[data-actionable='true'] { cursor: pointer; }

  .sz-interactive-logo[role='button']:focus-visible {
    border-radius: 18px;
    outline: 3px solid var(--sz-logo-accent);
    outline-offset: 7px;
  }

  .sz-interactive-logo__svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
  }

  .sz-interactive-logo__shell {
    transform: translate(
      calc(var(--sz-logo-pointer-x, 0) * 2px),
      calc(var(--sz-logo-pointer-y, 0) * 1.3px)
    );
    transform-origin: 48px 48px;
    transition: transform 420ms cubic-bezier(.2, .8, .2, 1);
    will-change: transform;
  }

  .sz-interactive-logo__mark {
    animation: sz-logo-idle 5.6s ease-in-out infinite;
    transform-box: fill-box;
    transform-origin: center;
  }

  .sz-interactive-logo__tile,
  .sz-interactive-logo__accent,
  .sz-interactive-logo__wordmark,
  .sz-interactive-logo__pulse,
  .sz-interactive-logo__status {
    transform-box: fill-box;
    transform-origin: center;
  }

  .sz-interactive-logo__tile {
    fill: var(--sz-logo-brand);
    transition: fill 280ms ease, opacity 260ms ease,
      transform 480ms cubic-bezier(.16, 1, .3, 1);
  }

  .sz-interactive-logo__accent {
    fill: var(--sz-logo-accent);
    transition: fill 280ms ease, transform 480ms cubic-bezier(.16, 1, .3, 1);
  }

  .sz-interactive-logo__wordmark {
    fill: var(--sz-logo-ink);
    transition: fill 280ms ease, opacity 280ms ease,
      transform 480ms cubic-bezier(.16, 1, .3, 1);
  }

  .sz-interactive-logo__pulse {
    fill: none;
    stroke: var(--sz-logo-accent);
    stroke-width: 2;
    opacity: 0;
    pointer-events: none;
    vector-effect: non-scaling-stroke;
  }

  .sz-interactive-logo__sheen {
    opacity: 0;
    pointer-events: none;
  }

  .sz-interactive-logo__status {
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 7;
    opacity: 0;
    pointer-events: none;
    vector-effect: non-scaling-stroke;
  }

  .sz-interactive-logo__success { stroke: var(--sz-logo-success); }
  .sz-interactive-logo__error { stroke: var(--sz-logo-error); }

  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__shell {
    transform: translate(
      calc(var(--sz-logo-pointer-x, 0) * 3px),
      calc(var(--sz-logo-pointer-y, 0) * 2px)
    ) scale(1.018);
  }

  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__tile--top { transform: translateY(-1.4px); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__tile--left { transform: translateX(-1.2px); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__tile--center { transform: scaleX(1.025); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__tile--right { transform: translateX(1.2px); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__tile--bottom { transform: translateY(1.4px); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__accent { transform: translate(1px, -1px) scale(1.08); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__wordmark { transform: translateX(2px); }
  .sz-interactive-logo[data-hovered='true'] .sz-interactive-logo__sheen {
    animation: sz-logo-sheen 820ms cubic-bezier(.2, .7, .2, 1) both;
  }

  .sz-interactive-logo[data-pressed='true'] .sz-interactive-logo__shell {
    transform: translate(
      calc(var(--sz-logo-pointer-x, 0) * 1px),
      calc(var(--sz-logo-pointer-y, 0) * .7px)
    ) scale(.945);
    transition-duration: 110ms;
  }

  .sz-interactive-logo[data-pressed='true'] .sz-interactive-logo__tile--top { transform: translateY(2px) scaleX(.96); }
  .sz-interactive-logo[data-pressed='true'] .sz-interactive-logo__tile--left { transform: translateX(2px); }
  .sz-interactive-logo[data-pressed='true'] .sz-interactive-logo__tile--center { transform: scaleX(.91); }
  .sz-interactive-logo[data-pressed='true'] .sz-interactive-logo__tile--right { transform: translateX(-2px); }
  .sz-interactive-logo[data-pressed='true'] .sz-interactive-logo__tile--bottom { transform: translateY(-2px) scaleX(.96); }

  .sz-interactive-logo[data-tap='true'] .sz-interactive-logo__pulse {
    animation: sz-logo-pulse 560ms cubic-bezier(.12, .8, .2, 1) both;
  }

  .sz-interactive-logo[data-tap='true'] .sz-interactive-logo__mark {
    animation: sz-logo-tap 540ms cubic-bezier(.2, 1.35, .35, 1) both;
  }

  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__mark { animation: none; }

  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__tile--top {
    animation: sz-logo-enter-top 820ms cubic-bezier(.16, 1, .3, 1) both;
  }
  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__tile--left {
    animation: sz-logo-enter-left 820ms 70ms cubic-bezier(.16, 1, .3, 1) both;
  }
  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__tile--center {
    animation: sz-logo-enter-center 760ms 130ms cubic-bezier(.16, 1, .3, 1) both;
  }
  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__tile--right {
    animation: sz-logo-enter-right 820ms 190ms cubic-bezier(.16, 1, .3, 1) both;
  }
  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__tile--bottom {
    animation: sz-logo-enter-bottom 820ms 240ms cubic-bezier(.16, 1, .3, 1) both;
  }
  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__accent {
    animation: sz-logo-enter-accent 700ms 360ms cubic-bezier(.16, 1.2, .3, 1) both;
  }
  .sz-interactive-logo[data-intro='true'] .sz-interactive-logo__wordmark {
    animation: sz-logo-wordmark-in 780ms 310ms cubic-bezier(.16, 1, .3, 1) both;
  }

  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__mark { animation: none; }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__tile,
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__accent {
    animation: sz-logo-loading 1.2s ease-in-out infinite;
  }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__tile--left { animation-delay: 90ms; }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__tile--center { animation-delay: 180ms; }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__tile--right { animation-delay: 270ms; }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__tile--bottom { animation-delay: 360ms; }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__accent { animation-delay: 450ms; }
  .sz-interactive-logo[data-state='loading'] .sz-interactive-logo__wordmark { opacity: .58; }

  .sz-interactive-logo[data-state='success'] .sz-interactive-logo__mark {
    animation: sz-logo-success-settle 700ms cubic-bezier(.16, 1, .3, 1) both;
  }
  .sz-interactive-logo[data-state='success'] .sz-interactive-logo__tile,
  .sz-interactive-logo[data-state='success'] .sz-interactive-logo__accent { opacity: .15; }
  .sz-interactive-logo[data-state='success'] .sz-interactive-logo__success {
    animation: sz-logo-status-draw 640ms 80ms cubic-bezier(.16, 1, .3, 1) both;
  }

  .sz-interactive-logo[data-state='error'] .sz-interactive-logo__mark {
    animation: sz-logo-error-shake 520ms cubic-bezier(.36, .07, .19, .97) both;
  }
  .sz-interactive-logo[data-state='error'] .sz-interactive-logo__tile,
  .sz-interactive-logo[data-state='error'] .sz-interactive-logo__accent { fill: var(--sz-logo-error); }
  .sz-interactive-logo[data-state='error'] .sz-interactive-logo__error {
    animation: sz-logo-status-appear 320ms 120ms ease-out both;
  }

  @keyframes sz-logo-idle {
    0%, 100% { transform: translateY(0) scale(1); }
    50% { transform: translateY(-1.25px) scale(1.006); }
  }
  @keyframes sz-logo-sheen {
    0% { opacity: 0; transform: translateX(-42px) skewX(-12deg); }
    24% { opacity: .72; }
    100% { opacity: 0; transform: translateX(110px) skewX(-12deg); }
  }
  @keyframes sz-logo-pulse {
    0% { opacity: .55; transform: scale(.76); }
    100% { opacity: 0; transform: scale(1.46); }
  }
  @keyframes sz-logo-tap {
    0% { transform: scale(.94); }
    46% { transform: scale(1.055); }
    100% { transform: scale(1); }
  }
  @keyframes sz-logo-enter-top {
    from { opacity: 0; transform: translate(-28px, -12px) scaleX(.58); }
  }
  @keyframes sz-logo-enter-left {
    from { opacity: 0; transform: translate(-14px, 18px) scaleY(.45); }
  }
  @keyframes sz-logo-enter-center {
    from { opacity: 0; transform: scaleX(.08); }
  }
  @keyframes sz-logo-enter-right {
    from { opacity: 0; transform: translate(14px, -18px) scaleY(.45); }
  }
  @keyframes sz-logo-enter-bottom {
    from { opacity: 0; transform: translate(28px, 12px) scaleX(.58); }
  }
  @keyframes sz-logo-enter-accent {
    from { opacity: 0; transform: scale(.05) rotate(-90deg); }
  }
  @keyframes sz-logo-wordmark-in {
    from { opacity: 0; transform: translateX(-18px); }
  }
  @keyframes sz-logo-loading {
    0%, 52%, 100% { opacity: .38; transform: translateY(0); }
    22% { opacity: 1; transform: translateY(-3px); }
  }
  @keyframes sz-logo-success-settle {
    0% { transform: scale(.94); }
    44% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
  @keyframes sz-logo-status-draw {
    0% { opacity: 0; stroke-dasharray: 80; stroke-dashoffset: 80; }
    100% { opacity: 1; stroke-dasharray: 80; stroke-dashoffset: 0; }
  }
  @keyframes sz-logo-status-appear {
    from { opacity: 0; transform: scale(.62); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes sz-logo-error-shake {
    0%, 100% { transform: translateX(0); }
    18%, 54% { transform: translateX(-4px); }
    36%, 72% { transform: translateX(4px); }
  }

  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__mark,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__tile,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__accent,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__wordmark,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__pulse,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__status,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__shell,
  .sz-interactive-logo[data-reduced-motion='true'] .sz-interactive-logo__sheen {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 1ms !important;
  }

  @media (prefers-reduced-motion: reduce) {
    .sz-interactive-logo__mark,
    .sz-interactive-logo__tile,
    .sz-interactive-logo__accent,
    .sz-interactive-logo__wordmark,
    .sz-interactive-logo__pulse,
    .sz-interactive-logo__status,
    .sz-interactive-logo__shell,
    .sz-interactive-logo__sheen {
      animation-duration: 1ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 1ms !important;
    }
  }
`;

function usePrefersReducedMotion(override: boolean | undefined) {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (
      override === true ||
      typeof window === "undefined" ||
      !window.matchMedia
    )
      return;

    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!query) return;
    const updatePreference = () => setPrefersReducedMotion(Boolean(query.matches));
    updatePreference();
    query.addEventListener?.("change", updatePreference);
    return () => query.removeEventListener?.("change", updatePreference);
  }, [override]);

  return override === true || prefersReducedMotion;
}

/**
 * Storeframe S interactive identity.
 *
 * Dependency-free React implementation using inline SVG and CSS animation.
 * The production-locked `StorizoneLogo` remains available separately.
 */
export const StorizoneInteractiveLogo = React.forwardRef<
  StorizoneInteractiveLogoHandle,
  StorizoneInteractiveLogoProps
>(function StorizoneInteractiveLogo(
  {
    variant = "lockup",
    state = "idle",
    theme = "light",
    interactive = true,
    playIntro = true,
    introKey,
    reducedMotion,
    width,
    label = "Storizone",
    colors,
    className,
    style,
    onClick,
    onPointerEnter,
    onPointerLeave,
    onPointerMove,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    onKeyDown,
    onKeyUp,
    onVisualStateChange,
    tabIndex,
    role,
    ...props
  },
  ref,
) {
  const rootRef = React.useRef<HTMLSpanElement>(null);
  const introTimer = React.useRef<ReturnType<typeof setTimeout>>();
  const tapTimer = React.useRef<ReturnType<typeof setTimeout>>();
  const introFrame = React.useRef<number>();
  const tapFrame = React.useRef<number>();
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const [introActive, setIntroActive] = React.useState(false);
  const [tapActive, setTapActive] = React.useState(false);
  const [pointer, setPointer] = React.useState({ x: 0, y: 0 });
  const visualStateCallback = React.useRef(onVisualStateChange);
  const motionReduced = usePrefersReducedMotion(reducedMotion);
  const instanceId = React.useId().replace(/:/g, "");
  const clipId = `sz-logo-clip-${instanceId}`;
  const sheenId = `sz-logo-sheen-${instanceId}`;
  const isActionable = Boolean(onClick);

  React.useEffect(() => {
    visualStateCallback.current = onVisualStateChange;
  }, [onVisualStateChange]);

  const notify = React.useCallback(
    (visualState: StorizoneInteractiveLogoVisualState) => {
      visualStateCallback.current?.(visualState);
    },
    [],
  );

  const replayIntro = React.useCallback(() => {
    if (introFrame.current !== undefined)
      cancelAnimationFrame(introFrame.current);
    clearTimeout(introTimer.current);
    setIntroActive(false);
    introFrame.current = requestAnimationFrame(() => {
      setIntroActive(true);
      notify("intro");
      introTimer.current = setTimeout(
        () => setIntroActive(false),
        motionReduced ? 20 : INTRO_DURATION,
      );
    });
  }, [motionReduced, notify]);

  const replayTap = React.useCallback(() => {
    if (tapFrame.current !== undefined) cancelAnimationFrame(tapFrame.current);
    clearTimeout(tapTimer.current);
    setTapActive(false);
    tapFrame.current = requestAnimationFrame(() => {
      setTapActive(true);
      notify("tap");
      tapTimer.current = setTimeout(
        () => setTapActive(false),
        motionReduced ? 20 : TAP_DURATION,
      );
    });
  }, [motionReduced, notify]);

  React.useImperativeHandle(
    ref,
    () => ({
      playIntro: replayIntro,
      playTap: replayTap,
      element: rootRef.current,
    }),
    [replayIntro, replayTap],
  );

  React.useEffect(() => {
    if (playIntro) replayIntro();
  }, [introKey, playIntro, replayIntro]);

  React.useEffect(() => {
    notify(state);
  }, [notify, state]);

  React.useEffect(() => {
    if (!interactive) {
      setHovered(false);
      setPressed(false);
      setPointer({ x: 0, y: 0 });
    }
  }, [interactive]);

  React.useEffect(
    () => () => {
      clearTimeout(introTimer.current);
      clearTimeout(tapTimer.current);
      if (introFrame.current !== undefined)
        cancelAnimationFrame(introFrame.current);
      if (tapFrame.current !== undefined)
        cancelAnimationFrame(tapFrame.current);
    },
    [],
  );

  const palette = theme === "dark" ? DARK_COLORS : LIGHT_COLORS;
  const logoStyle: LogoStyle = {
    "--sz-logo-brand": colors?.brand ?? palette.brand,
    "--sz-logo-accent": colors?.accent ?? palette.accent,
    "--sz-logo-ink": colors?.ink ?? palette.ink,
    "--sz-logo-success": colors?.success ?? palette.success,
    "--sz-logo-error": colors?.error ?? palette.error,
    "--sz-logo-pointer-x": pointer.x,
    "--sz-logo-pointer-y": pointer.y,
    width: width ?? (variant === "mark" ? 96 : 410),
    ...style,
  };

  const handlePointerEnter = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (interactive) {
      setHovered(true);
      notify("hover");
    }
    onPointerEnter?.(event);
  };

  const releasePointer = () => {
    setPressed(false);
    setPointer({ x: 0, y: 0 });
  };

  const handlePointerLeave = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (interactive) {
      setHovered(false);
      releasePointer();
      notify(state);
    }
    onPointerLeave?.(event);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (interactive && !motionReduced) {
      const bounds = event.currentTarget.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        setPointer({
          x: Number(
            (((event.clientX - bounds.left) / bounds.width - 0.5) * 2).toFixed(
              3,
            ),
          ),
          y: Number(
            (((event.clientY - bounds.top) / bounds.height - 0.5) * 2).toFixed(
              3,
            ),
          ),
        });
      }
    }
    onPointerMove?.(event);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (interactive && event.button !== 2) {
      setPressed(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
      notify("pressed");
    }
    onPointerDown?.(event);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLSpanElement>) => {
    if (interactive && pressed) {
      setPressed(false);
    }
    onPointerUp?.(event);
  };

  const handlePointerCancel = (event: React.PointerEvent<HTMLSpanElement>) => {
    releasePointer();
    onPointerCancel?.(event);
  };

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (interactive) replayTap();
    onClick?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (
      isActionable &&
      interactive &&
      (event.key === "Enter" || event.key === " ")
    ) {
      event.preventDefault();
      if (!event.repeat) {
        setPressed(true);
        notify("pressed");
      }
    }
    onKeyDown?.(event);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (isActionable && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setPressed(false);
      event.currentTarget.click();
    }
    onKeyUp?.(event);
  };

  return (
    <span
      ref={rootRef}
      className={cn("sz-interactive-logo", className)}
      style={logoStyle}
      data-actionable={isActionable || undefined}
      data-hovered={hovered || undefined}
      data-pressed={pressed || undefined}
      data-intro={introActive || undefined}
      data-tap={tapActive || undefined}
      data-state={state}
      data-theme={theme}
      data-reduced-motion={motionReduced || undefined}
      role={role ?? (isActionable ? "button" : "img")}
      tabIndex={tabIndex ?? (isActionable ? 0 : undefined)}
      aria-label={props["aria-label"] ?? label}
      aria-busy={state === "loading" || undefined}
      onClick={handleClick}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      {...props}
    >
      <svg
        className="sz-interactive-logo__svg"
        viewBox={variant === "mark" ? "0 0 96 96" : "0 0 410 96"}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <style>{LOGO_STYLES}</style>
        <defs>
          <linearGradient id={sheenId} x1="0" y1="0" x2="1" y2="0">
            <stop stopColor="white" stopOpacity="0" />
            <stop offset=".5" stopColor="white" stopOpacity=".72" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <clipPath id={clipId}>
            <rect x="18" y="12" width="60" height="16" rx="8" />
            <rect x="18" y="20" width="16" height="29" rx="8" />
            <rect x="18" y="40" width="60" height="16" rx="8" />
            <rect x="62" y="48" width="16" height="29" rx="8" />
            <rect x="18" y="68" width="60" height="16" rx="8" />
          </clipPath>
        </defs>

        <g className="sz-interactive-logo__shell">
          <g className="sz-interactive-logo__mark">
            <circle
              className="sz-interactive-logo__pulse"
              cx="48"
              cy="48"
              r="39"
            />
            <rect
              className="sz-interactive-logo__tile sz-interactive-logo__tile--top"
              x="18"
              y="12"
              width="60"
              height="16"
              rx="8"
            />
            <rect
              className="sz-interactive-logo__tile sz-interactive-logo__tile--left"
              x="18"
              y="20"
              width="16"
              height="29"
              rx="8"
            />
            <rect
              className="sz-interactive-logo__tile sz-interactive-logo__tile--center"
              x="18"
              y="40"
              width="60"
              height="16"
              rx="8"
            />
            <rect
              className="sz-interactive-logo__tile sz-interactive-logo__tile--right"
              x="62"
              y="48"
              width="16"
              height="29"
              rx="8"
            />
            <rect
              className="sz-interactive-logo__tile sz-interactive-logo__tile--bottom"
              x="18"
              y="68"
              width="60"
              height="16"
              rx="8"
            />
            <rect
              className="sz-interactive-logo__accent"
              x="62"
              y="12"
              width="16"
              height="16"
              rx="8"
            />
            <rect
              className="sz-interactive-logo__sheen"
              x="-36"
              y="8"
              width="18"
              height="78"
              fill={`url(#${sheenId})`}
              clipPath={`url(#${clipId})`}
            />
            <path
              className="sz-interactive-logo__status sz-interactive-logo__success"
              d="M26 48L39 60L68 29"
            />
            <path
              className="sz-interactive-logo__status sz-interactive-logo__error"
              d="M48 27V54M48 68V69"
            />
          </g>

          {variant === "lockup" ? (
            <text
              className="sz-interactive-logo__wordmark"
              x="106"
              y="65"
              fontFamily="Inter, 'Segoe UI', Arial, sans-serif"
              fontSize="48"
              fontWeight="700"
              letterSpacing="-1.8"
            >
              storizone
            </text>
          ) : null}
        </g>
      </svg>
    </span>
  );
});

StorizoneInteractiveLogo.displayName = "StorizoneInteractiveLogo";
